import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Upload, Play, Pause, Trash2, Edit2, Save, X,
  Music, Drum, Guitar, Wind, Piano, Volume2, VolumeX,
  FolderOpen, Plus, Search, Filter, Grid, List, ChevronDown,
  Layers, RefreshCw, Download, Info, Settings, PlusCircle, Check,
  Library, FileAudio
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { MediaLibraryPicker } from '@/components/admin/MediaLibraryPicker';

const API = process.env.REACT_APP_BACKEND_URL;

// Instrument category icons
const CATEGORY_ICONS = {
  percussion: Drum,
  strings: Guitar,
  brass: Wind,
  woodwinds: Wind,
  keys: Piano,
  default: Music,
};

// Velocity layer colors
const VELOCITY_COLORS = {
  soft: 'bg-blue-500',
  medium: 'bg-yellow-500',
  loud: 'bg-red-500',
};

const AdminSampleManager = () => {
  const [samples, setSamples] = useState([]);
  const [categories, setCategories] = useState({});
  const [fullCategories, setFullCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [showEditMode, setShowEditMode] = useState(false);
  const [playingSample, setPlayingSample] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('percussion');
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadMode, setUploadMode] = useState('upload'); // 'upload' or 'library'
  
  // Edit mode state
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingInstrument, setEditingInstrument] = useState(null);
  const [newCategoryForm, setNewCategoryForm] = useState({ id: '', name: '', icon: 'music' });
  const [newInstrumentForm, setNewInstrumentForm] = useState({ id: '', name: '', description: '' });
  const [showNewCategoryForm, setShowNewCategoryForm] = useState(false);
  const [showNewInstrumentForm, setShowNewInstrumentForm] = useState(null); // category id
  
  // Selected media from library
  const [selectedMediaFiles, setSelectedMediaFiles] = useState([]);
  
  const audioRef = useRef(new Audio());
  const fileInputRef = useRef(null);
  
  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    files: [],
    category: 'percussion',
    instrument: 'surdo',
    sampleType: 'one_shot',
    velocityLayer: 'medium',
    roundRobinIndex: 0,
    bpm: '',
    key: '',
    notes: '',
  });

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
    fetchSamples();
  }, []);

  // Cleanup audio on unmount
  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      audio.pause();
      audio.src = '';
    };
  }, []);
  
  // Prevent browser from opening dropped files globally (Safari compatibility)
  useEffect(() => {
    const preventDefaults = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'none';
      }
      return false;
    };
    
    const events = ['dragenter', 'dragover', 'dragleave', 'drop'];
    events.forEach(eventName => {
      document.addEventListener(eventName, preventDefaults, { capture: true });
      document.body.addEventListener(eventName, preventDefaults, { capture: true });
    });
    
    return () => {
      events.forEach(eventName => {
        document.removeEventListener(eventName, preventDefaults, { capture: true });
        document.body.removeEventListener(eventName, preventDefaults, { capture: true });
      });
    };
  }, []);

  const fetchCategories = async () => {
    try {
      const [simpleRes, fullRes] = await Promise.all([
        fetch(`${API}/api/samples/categories`),
        fetch(`${API}/api/samples/categories/full`)
      ]);
      
      if (simpleRes.ok) {
        const data = await simpleRes.json();
        setCategories(data);
      }
      if (fullRes.ok) {
        const fullData = await fullRes.json();
        setFullCategories(fullData);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchSamples = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API}/api/samples`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSamples(data);
      }
    } catch (error) {
      console.error('Error fetching samples:', error);
      toast.error('Failed to load samples');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setUploadForm(prev => ({ ...prev, files }));
  };

  const handleUpload = async () => {
    if (uploadForm.files.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    setUploading(true);
    const token = localStorage.getItem('auth_token');
    let successCount = 0;

    for (const file of uploadForm.files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', file.name.replace(/\.[^/.]+$/, '')); // Remove extension
      formData.append('category', uploadForm.category);
      formData.append('instrument', uploadForm.instrument);
      formData.append('sample_type', uploadForm.sampleType);
      formData.append('velocity_layer', uploadForm.velocityLayer);
      formData.append('round_robin_index', uploadForm.roundRobinIndex);
      if (uploadForm.bpm) formData.append('bpm', uploadForm.bpm);
      if (uploadForm.key) formData.append('key', uploadForm.key);
      if (uploadForm.notes) formData.append('notes', uploadForm.notes);

      try {
        const response = await fetch(`${API}/api/samples/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });

        if (response.ok) {
          successCount++;
        } else {
          const error = await response.json();
          toast.error(`Failed to upload ${file.name}: ${error.detail}`);
        }
      } catch (error) {
        toast.error(`Error uploading ${file.name}`);
      }
    }

    setUploading(false);
    if (successCount > 0) {
      toast.success(`Uploaded ${successCount} sample(s)`);
      fetchSamples();
      setShowUploadModal(false);
      setUploadForm(prev => ({ ...prev, files: [] }));
    }
  };

  // Import samples from Media Library
  const handleImportFromLibrary = async () => {
    if (selectedMediaFiles.length === 0) {
      toast.error('Please select files from the library');
      return;
    }

    setUploading(true);
    const token = localStorage.getItem('auth_token');
    let successCount = 0;

    for (const mediaFile of selectedMediaFiles) {
      const formData = new FormData();
      // We'll pass the media URL to the backend to copy the file
      formData.append('media_url', mediaFile.url);
      formData.append('name', mediaFile.filename?.replace(/\.[^/.]+$/, '') || mediaFile.name || 'Sample');
      formData.append('category', uploadForm.category);
      formData.append('instrument', uploadForm.instrument);
      formData.append('sample_type', uploadForm.sampleType);
      formData.append('velocity_layer', uploadForm.velocityLayer);
      formData.append('round_robin_index', uploadForm.roundRobinIndex);
      if (uploadForm.bpm) formData.append('bpm', uploadForm.bpm);
      if (uploadForm.key) formData.append('key', uploadForm.key);
      if (uploadForm.notes) formData.append('notes', uploadForm.notes);

      try {
        const response = await fetch(`${API}/api/samples/import-from-library`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });

        if (response.ok) {
          successCount++;
        } else {
          const error = await response.json();
          toast.error(`Failed to import: ${error.detail}`);
        }
      } catch (error) {
        toast.error(`Error importing sample`);
      }
    }

    setUploading(false);
    if (successCount > 0) {
      toast.success(`Imported ${successCount} sample(s) from library`);
      fetchSamples();
      setShowUploadModal(false);
      setSelectedMediaFiles([]);
    }
  };

  // Handle media library selection
  const handleMediaLibrarySelect = (selectedMedia) => {
    // selectedMedia is an array of media items from the library
    setSelectedMediaFiles(Array.isArray(selectedMedia) ? selectedMedia : [selectedMedia]);
    setShowMediaLibrary(false);
  };

  const handleDelete = async (sampleId) => {
    if (!window.confirm('Delete this sample?')) return;

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API}/api/samples/${sampleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Sample deleted');
        fetchSamples();
      } else {
        toast.error('Failed to delete sample');
      }
    } catch (error) {
      toast.error('Error deleting sample');
    }
  };

  const playSample = (sample) => {
    if (playingSample === sample.id) {
      audioRef.current.pause();
      setPlayingSample(null);
    } else {
      audioRef.current.src = `${API}${sample.url}`;
      audioRef.current.play();
      setPlayingSample(sample.id);
      
      audioRef.current.onended = () => setPlayingSample(null);
    }
  };

  // ==================== Category & Instrument Management ====================
  
  const handleAddCategory = async () => {
    if (!newCategoryForm.id || !newCategoryForm.name) {
      toast.error('Please fill in category ID and name');
      return;
    }
    
    try {
      const token = localStorage.getItem('auth_token');
      const formData = new FormData();
      formData.append('id', newCategoryForm.id.toLowerCase().replace(/\s+/g, '_'));
      formData.append('name', newCategoryForm.name);
      formData.append('icon', newCategoryForm.icon);
      
      const response = await fetch(`${API}/api/samples/categories`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        toast.success('Category added');
        fetchCategories();
        setNewCategoryForm({ id: '', name: '', icon: 'music' });
        setShowNewCategoryForm(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add category');
      }
    } catch (error) {
      toast.error('Error adding category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm(`Delete category "${categoryId}" and all its instruments?`)) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API}/api/samples/categories/${categoryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Category deleted');
        fetchCategories();
        if (selectedCategory === categoryId) {
          setSelectedCategory('all');
        }
      } else {
        toast.error('Failed to delete category');
      }
    } catch (error) {
      toast.error('Error deleting category');
    }
  };

  const handleAddInstrument = async (categoryId) => {
    if (!newInstrumentForm.id || !newInstrumentForm.name) {
      toast.error('Please fill in instrument ID and name');
      return;
    }
    
    try {
      const token = localStorage.getItem('auth_token');
      const formData = new FormData();
      formData.append('id', newInstrumentForm.id.toLowerCase().replace(/\s+/g, '_'));
      formData.append('name', newInstrumentForm.name);
      formData.append('description', newInstrumentForm.description || '');
      
      const response = await fetch(`${API}/api/samples/categories/${categoryId}/instruments`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        toast.success('Instrument added');
        fetchCategories();
        setNewInstrumentForm({ id: '', name: '', description: '' });
        setShowNewInstrumentForm(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add instrument');
      }
    } catch (error) {
      toast.error('Error adding instrument');
    }
  };

  const handleDeleteInstrument = async (categoryId, instrumentId) => {
    if (!window.confirm(`Delete instrument "${instrumentId}" and all its samples?`)) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API}/api/samples/categories/${categoryId}/instruments/${instrumentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Instrument deleted');
        fetchCategories();
        fetchSamples();
      } else {
        toast.error('Failed to delete instrument');
      }
    } catch (error) {
      toast.error('Error deleting instrument');
    }
  };

  // Filter samples
  const filteredSamples = samples.filter(sample => {
    const matchesCategory = selectedCategory === 'all' || sample.category === selectedCategory;
    const matchesInstrument = !selectedInstrument || sample.instrument === selectedInstrument;
    const matchesSearch = !searchQuery || 
      sample.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sample.instrument.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesInstrument && matchesSearch;
  });

  // Group samples by instrument
  const samplesByInstrument = filteredSamples.reduce((acc, sample) => {
    if (!acc[sample.instrument]) {
      acc[sample.instrument] = [];
    }
    acc[sample.instrument].push(sample);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5 text-foreground" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <Drum className="w-6 h-6 text-orange-400" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-foreground">Sample Manager</h1>
                <p className="text-sm text-muted-foreground">Upload and manage instrument samples</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Link
              to="/admin/score-creator"
              className="px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg font-medium flex items-center gap-2 transition-colors"
            >
              <Music className="w-4 h-4" />
              Score Creator
            </Link>
            <button
              onClick={() => setShowEditMode(!showEditMode)}
              className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors ${
                showEditMode 
                  ? 'bg-green-500 text-white' 
                  : 'bg-white/10 hover:bg-white/20 text-foreground'
              }`}
            >
              <Settings className="w-4 h-4" />
              {showEditMode ? 'Done Editing' : 'Edit Library'}
            </button>
            <button
              onClick={fetchSamples}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-5 h-5 text-muted-foreground ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium flex items-center gap-2 hover:bg-primary/90 transition-colors"
            >
              <Upload className="w-4 h-4" />
              Upload Samples
            </button>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar - Categories */}
        <div className="w-72 bg-card border-r border-border p-4 min-h-[calc(100vh-73px)] overflow-y-auto">
          {showEditMode && (
            <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
              <p className="text-sm text-green-400 font-medium flex items-center gap-2">
                <Edit2 className="w-4 h-4" />
                Edit Mode Active
              </p>
              <p className="text-xs text-muted-foreground mt-1">Click items to edit or delete</p>
            </div>
          )}
          
          <div className="space-y-2">
            {!showEditMode && (
              <button
                onClick={() => { setSelectedCategory('all'); setSelectedInstrument(null); }}
                className={`w-full px-3 py-2 rounded-lg text-left flex items-center gap-2 transition-colors ${
                  selectedCategory === 'all' ? 'bg-primary/20 text-primary' : 'hover:bg-white/10 text-foreground'
                }`}
              >
                <FolderOpen className="w-4 h-4" />
                All Samples
                <span className="ml-auto text-xs text-muted-foreground">{samples.length}</span>
              </button>
            )}
            
            {Object.entries(categories).map(([catKey, instruments]) => {
              const Icon = CATEGORY_ICONS[catKey] || CATEGORY_ICONS.default;
              const catSamples = samples.filter(s => s.category === catKey);
              const catInfo = fullCategories.find(c => c.id === catKey);
              
              return (
                <div key={catKey} className="relative group">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => { setSelectedCategory(catKey); setSelectedInstrument(null); }}
                      className={`flex-1 px-3 py-2 rounded-lg text-left flex items-center gap-2 transition-colors ${
                        selectedCategory === catKey ? 'bg-primary/20 text-primary' : 'hover:bg-white/10 text-foreground'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="capitalize">{catInfo?.name || catKey}</span>
                      <span className="ml-auto text-xs text-muted-foreground">{catSamples.length}</span>
                    </button>
                    
                    {showEditMode && (
                      <button
                        onClick={() => handleDeleteCategory(catKey)}
                        className="p-1.5 hover:bg-red-500/20 rounded text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Delete category"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  
                  {selectedCategory === catKey && (
                    <div className="ml-4 mt-1 space-y-1 border-l-2 border-border pl-2">
                      {Object.entries(instruments).map(([instKey, instInfo]) => {
                        const instSamples = samples.filter(s => s.instrument === instKey);
                        return (
                          <div key={instKey} className="flex items-center gap-1 group/inst">
                            <button
                              onClick={() => setSelectedInstrument(selectedInstrument === instKey ? null : instKey)}
                              className={`flex-1 px-3 py-1.5 rounded text-left text-sm transition-colors ${
                                selectedInstrument === instKey 
                                  ? 'bg-white/20 text-foreground' 
                                  : 'hover:bg-white/10 text-muted-foreground'
                              }`}
                            >
                              {instInfo.name}
                              <span className="ml-1 text-xs opacity-60">({instSamples.length})</span>
                            </button>
                            
                            {showEditMode && (
                              <button
                                onClick={() => handleDeleteInstrument(catKey, instKey)}
                                className="p-1 hover:bg-red-500/20 rounded text-red-400 opacity-0 group-hover/inst:opacity-100 transition-opacity"
                                title="Delete instrument"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                      
                      {/* Add New Instrument */}
                      {showEditMode && (
                        <>
                          {showNewInstrumentForm === catKey ? (
                            <div className="p-2 bg-white/5 rounded-lg space-y-2 mt-2">
                              <input
                                type="text"
                                placeholder="ID (e.g., zabumba)"
                                value={newInstrumentForm.id}
                                onChange={(e) => setNewInstrumentForm(prev => ({ ...prev, id: e.target.value }))}
                                className="w-full px-2 py-1 bg-background border border-border rounded text-sm text-foreground"
                              />
                              <input
                                type="text"
                                placeholder="Name (e.g., Zabumba)"
                                value={newInstrumentForm.name}
                                onChange={(e) => setNewInstrumentForm(prev => ({ ...prev, name: e.target.value }))}
                                className="w-full px-2 py-1 bg-background border border-border rounded text-sm text-foreground"
                              />
                              <input
                                type="text"
                                placeholder="Description (optional)"
                                value={newInstrumentForm.description}
                                onChange={(e) => setNewInstrumentForm(prev => ({ ...prev, description: e.target.value }))}
                                className="w-full px-2 py-1 bg-background border border-border rounded text-sm text-foreground"
                              />
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleAddInstrument(catKey)}
                                  className="flex-1 px-2 py-1 bg-green-500 text-white rounded text-xs flex items-center justify-center gap-1"
                                >
                                  <Check className="w-3 h-3" /> Add
                                </button>
                                <button
                                  onClick={() => { setShowNewInstrumentForm(null); setNewInstrumentForm({ id: '', name: '', description: '' }); }}
                                  className="px-2 py-1 bg-white/10 text-foreground rounded text-xs"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => setShowNewInstrumentForm(catKey)}
                              className="w-full px-3 py-1.5 rounded text-left text-sm text-green-400 hover:bg-green-500/10 transition-colors flex items-center gap-2"
                            >
                              <PlusCircle className="w-3 h-3" />
                              Add Instrument
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            
            {/* Add New Category */}
            {showEditMode && (
              <div className="mt-4 pt-4 border-t border-border">
                {showNewCategoryForm ? (
                  <div className="p-3 bg-white/5 rounded-lg space-y-2">
                    <input
                      type="text"
                      placeholder="ID (e.g., ethnic)"
                      value={newCategoryForm.id}
                      onChange={(e) => setNewCategoryForm(prev => ({ ...prev, id: e.target.value }))}
                      className="w-full px-2 py-1 bg-background border border-border rounded text-sm text-foreground"
                    />
                    <input
                      type="text"
                      placeholder="Name (e.g., Ethnic)"
                      value={newCategoryForm.name}
                      onChange={(e) => setNewCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-2 py-1 bg-background border border-border rounded text-sm text-foreground"
                    />
                    <select
                      value={newCategoryForm.icon}
                      onChange={(e) => setNewCategoryForm(prev => ({ ...prev, icon: e.target.value }))}
                      className="w-full px-2 py-1 bg-background border border-border rounded text-sm text-foreground"
                    >
                      <option value="music">Music (default)</option>
                      <option value="drum">Drum</option>
                      <option value="guitar">Guitar/Strings</option>
                      <option value="wind">Wind</option>
                      <option value="piano">Piano/Keys</option>
                    </select>
                    <div className="flex gap-2">
                      <button
                        onClick={handleAddCategory}
                        className="flex-1 px-2 py-1 bg-green-500 text-white rounded text-sm flex items-center justify-center gap-1"
                      >
                        <Check className="w-3 h-3" /> Add Category
                      </button>
                      <button
                        onClick={() => { setShowNewCategoryForm(false); setNewCategoryForm({ id: '', name: '', icon: 'music' }); }}
                        className="px-2 py-1 bg-white/10 text-foreground rounded text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowNewCategoryForm(true)}
                    className="w-full px-3 py-2 rounded-lg text-left text-green-400 hover:bg-green-500/10 transition-colors flex items-center gap-2"
                  >
                    <PlusCircle className="w-4 h-4" />
                    Add New Category
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6">
          {/* Search & Filters */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search samples..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg text-foreground"
              />
            </div>
            
            <div className="flex items-center gap-2 bg-card border border-border rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-primary/20 text-primary' : 'text-muted-foreground'}`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-primary/20 text-primary' : 'text-muted-foreground'}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Sample Grid/List */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : filteredSamples.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <Music className="w-16 h-16 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No samples yet</h3>
              <p className="text-muted-foreground mb-4">Upload your instrument samples to get started</p>
              <button
                onClick={() => setShowUploadModal(true)}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Upload Samples
              </button>
            </div>
          ) : (
            <div className="space-y-8">
              {Object.entries(samplesByInstrument).map(([instrument, instrumentSamples]) => (
                <div key={instrument}>
                  <h3 className="text-lg font-semibold text-foreground mb-4 capitalize flex items-center gap-2">
                    <Drum className="w-5 h-5 text-orange-400" />
                    {categories[selectedCategory]?.[instrument]?.name || instrument}
                    <span className="text-sm font-normal text-muted-foreground">
                      ({instrumentSamples.length} samples)
                    </span>
                  </h3>
                  
                  <div className={viewMode === 'grid' 
                    ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4' 
                    : 'space-y-2'
                  }>
                    {instrumentSamples.map(sample => (
                      <SampleCard
                        key={sample.id}
                        sample={sample}
                        isPlaying={playingSample === sample.id}
                        onPlay={() => playSample(sample)}
                        onDelete={() => handleDelete(sample.id)}
                        viewMode={viewMode}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      <AnimatePresence>
        {showUploadModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            onClick={() => setShowUploadModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-card rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-500/20 rounded-lg">
                    <Layers className="w-6 h-6 text-orange-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-foreground">Add Samples</h2>
                    <p className="text-sm text-muted-foreground">WAV, MP3, AIFF, or M4A files</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="p-2 hover:bg-white/10 rounded-lg"
                >
                  <X className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>

              {/* Source Selection Tabs */}
              <div className="flex gap-2 mb-6">
                <button
                  onClick={() => { setUploadMode('upload'); setSelectedMediaFiles([]); }}
                  className={`flex-1 px-4 py-3 rounded-xl flex items-center justify-center gap-2 transition-all ${
                    uploadMode === 'upload'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-white/5 hover:bg-white/10 text-muted-foreground'
                  }`}
                >
                  <Upload className="w-5 h-5" />
                  <span className="font-medium">Upload New Files</span>
                </button>
                <button
                  onClick={() => { setUploadMode('library'); setUploadForm(prev => ({ ...prev, files: [] })); }}
                  className={`flex-1 px-4 py-3 rounded-xl flex items-center justify-center gap-2 transition-all ${
                    uploadMode === 'library'
                      ? 'bg-purple-500 text-white'
                      : 'bg-white/5 hover:bg-white/10 text-muted-foreground'
                  }`}
                >
                  <Library className="w-5 h-5" />
                  <span className="font-medium">From Media Library</span>
                </button>
              </div>

              <div className="space-y-4">
                {/* Upload Mode: File Drop Zone */}
                {uploadMode === 'upload' && (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.add('border-primary', 'bg-primary/10'); }}
                    onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.remove('border-primary', 'bg-primary/10'); }}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'copy'; }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.currentTarget.classList.remove('border-primary', 'bg-primary/10');
                      const files = Array.from(e.dataTransfer.files).filter(f => 
                        f.type.startsWith('audio/') || f.name.match(/\.(wav|mp3|aiff|aif|m4a|aac)$/i)
                      );
                      if (files.length > 0) {
                        setUploadForm(prev => ({ ...prev, files }));
                        toast.success(`${files.length} audio file(s) ready to upload`);
                      } else {
                        toast.error('Please drop audio files (WAV, MP3, AIFF, M4A)');
                      }
                    }}
                    className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept=".wav,.mp3,.aiff,.aif,.m4a,.aac,audio/*"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <Upload className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-foreground font-medium">Click to select files</p>
                    <p className="text-sm text-muted-foreground">WAV, MP3, AIFF, or M4A</p>
                    
                    {uploadForm.files.length > 0 && (
                      <div className="mt-4 p-3 bg-primary/10 rounded-lg">
                        <p className="text-primary font-medium">{uploadForm.files.length} file(s) selected</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {uploadForm.files.map(f => f.name).join(', ')}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Library Mode: Browse Library Button */}
                {uploadMode === 'library' && (
                  <div className="space-y-4">
                    <button
                      onClick={() => setShowMediaLibrary(true)}
                      className="w-full border-2 border-dashed border-purple-500/50 rounded-xl p-8 text-center cursor-pointer hover:border-purple-500 hover:bg-purple-500/10 transition-colors"
                    >
                      <Library className="w-12 h-12 text-purple-400 mx-auto mb-4" />
                      <p className="text-foreground font-medium">Browse Media Library</p>
                      <p className="text-sm text-muted-foreground">Select audio files from your library</p>
                    </button>
                    
                    {selectedMediaFiles.length > 0 && (
                      <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl">
                        <p className="text-purple-400 font-medium mb-2">
                          {selectedMediaFiles.length} file(s) selected from library
                        </p>
                        <div className="space-y-2 max-h-32 overflow-y-auto">
                          {selectedMediaFiles.map((file, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm">
                              <FileAudio className="w-4 h-4 text-purple-400" />
                              <span className="text-foreground truncate">{file.filename || file.name}</span>
                              <button
                                onClick={() => setSelectedMediaFiles(prev => prev.filter((_, i) => i !== idx))}
                                className="ml-auto p-1 hover:bg-red-500/20 rounded text-red-400"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Category & Instrument */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Category</label>
                    <select
                      value={uploadForm.category}
                      onChange={(e) => {
                        const cat = e.target.value;
                        const firstInst = Object.keys(categories[cat] || {})[0] || '';
                        setUploadForm(prev => ({ ...prev, category: cat, instrument: firstInst }));
                      }}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                    >
                      {Object.keys(categories).map(cat => (
                        <option key={cat} value={cat} className="capitalize">{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Instrument</label>
                    <select
                      value={uploadForm.instrument}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, instrument: e.target.value }))}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                    >
                      {Object.entries(categories[uploadForm.category] || {}).map(([key, info]) => (
                        <option key={key} value={key}>{info.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Sample Type & Velocity */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Sample Type</label>
                    <select
                      value={uploadForm.sampleType}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, sampleType: e.target.value }))}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                    >
                      <option value="one_shot">One-Shot (Single Hit)</option>
                      <option value="loop">Loop (One Bar)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Velocity Layer</label>
                    <select
                      value={uploadForm.velocityLayer}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, velocityLayer: e.target.value }))}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                    >
                      <option value="soft">Soft (pp-mp)</option>
                      <option value="medium">Medium (mf)</option>
                      <option value="loud">Loud (f-ff)</option>
                    </select>
                  </div>
                </div>

                {/* Round Robin & BPM */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Round Robin Index</label>
                    <input
                      type="number"
                      min="0"
                      max="9"
                      value={uploadForm.roundRobinIndex}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, roundRobinIndex: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                    />
                    <p className="text-xs text-muted-foreground mt-1">For multiple variations (0-9)</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">BPM (for loops)</label>
                    <input
                      type="number"
                      min="60"
                      max="200"
                      value={uploadForm.bpm}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, bpm: e.target.value }))}
                      placeholder="e.g., 120"
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                    />
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Notes (optional)</label>
                  <textarea
                    value={uploadForm.notes}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Any additional notes about these samples..."
                    rows={2}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground resize-none"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => { setShowUploadModal(false); setSelectedMediaFiles([]); setUploadForm(prev => ({ ...prev, files: [] })); }}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 text-foreground rounded-lg transition-colors"
                >
                  Cancel
                </button>
                
                {uploadMode === 'upload' ? (
                  <button
                    onClick={handleUpload}
                    disabled={uploading || uploadForm.files.length === 0}
                    className="px-6 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {uploading ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Upload {uploadForm.files.length > 0 ? `(${uploadForm.files.length})` : ''}
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={handleImportFromLibrary}
                    disabled={uploading || selectedMediaFiles.length === 0}
                    className="px-6 py-2 bg-purple-500 text-white rounded-lg font-medium hover:bg-purple-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {uploading ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Library className="w-4 h-4" />
                        Import {selectedMediaFiles.length > 0 ? `(${selectedMediaFiles.length})` : ''}
                      </>
                    )}
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Media Library Picker */}
      <MediaLibraryPicker
        isOpen={showMediaLibrary}
        onClose={() => setShowMediaLibrary(false)}
        onSelect={handleMediaLibrarySelect}
        allowedTypes={['audio']}
        multiple={true}
      />
    </div>
  );
};

// Sample Card Component
const SampleCard = ({ sample, isPlaying, onPlay, onDelete, viewMode }) => {
  if (viewMode === 'list') {
    return (
      <div className="flex items-center gap-4 p-3 bg-card border border-border rounded-lg hover:border-primary/30 transition-colors">
        <button
          onClick={onPlay}
          className={`p-2 rounded-full transition-colors ${
            isPlaying ? 'bg-primary text-primary-foreground' : 'bg-white/10 hover:bg-white/20 text-foreground'
          }`}
        >
          {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
        </button>
        
        <div className="flex-1 min-w-0">
          <p className="font-medium text-foreground truncate">{sample.name}</p>
          <p className="text-xs text-muted-foreground">
            {sample.sample_type === 'loop' ? 'Loop' : 'One-Shot'} • {sample.velocity_layer}
            {sample.bpm && ` • ${sample.bpm} BPM`}
          </p>
        </div>
        
        <span className={`px-2 py-0.5 rounded text-xs text-white ${VELOCITY_COLORS[sample.velocity_layer]}`}>
          {sample.velocity_layer}
        </span>
        
        {sample.round_robin_index > 0 && (
          <span className="text-xs text-muted-foreground">RR{sample.round_robin_index}</span>
        )}
        
        <button
          onClick={onDelete}
          className="p-2 hover:bg-red-500/20 rounded text-red-400"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl p-4 hover:border-primary/30 transition-colors">
      <div className="flex items-start justify-between mb-3">
        <div className={`px-2 py-1 rounded text-xs text-white ${VELOCITY_COLORS[sample.velocity_layer]}`}>
          {sample.velocity_layer}
        </div>
        <button
          onClick={onDelete}
          className="p-1.5 hover:bg-red-500/20 rounded text-red-400"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      
      <button
        onClick={onPlay}
        className={`w-full aspect-square rounded-lg flex items-center justify-center mb-3 transition-colors ${
          isPlaying 
            ? 'bg-primary text-primary-foreground' 
            : 'bg-white/5 hover:bg-white/10 text-foreground'
        }`}
      >
        {isPlaying ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8 ml-1" />}
      </button>
      
      <h4 className="font-medium text-foreground truncate" title={sample.name}>{sample.name}</h4>
      <p className="text-xs text-muted-foreground mt-1">
        {sample.sample_type === 'loop' ? 'Loop' : 'One-Shot'}
        {sample.round_robin_index > 0 && ` • RR${sample.round_robin_index}`}
        {sample.bpm && ` • ${sample.bpm} BPM`}
      </p>
    </div>
  );
};

export default AdminSampleManager;
